import { OptimizedImage } from "@/components/ui/optimized-image";
import {
  Ruler,
  Zap,
  Gauge,
  Package,
  ShieldCheck,
  Settings2,
  Droplets,
  Sparkles,
  Radio,
  Wrench,
  CheckCircle2,
} from "lucide-react";

export interface SpecGroup {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  rows: Array<{ label: string; value: string }>;
}

export interface DimensionMetric {
  label: string;
  value: string;
  unit: string;
}

export interface InBoxItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  qty?: string;
}

interface Rem7HydraSpecsPanelProps {
  shortName: string;
  productName: string;
  sku: string;
  heroImage: { src: string; alt: string };
  dimensions: DimensionMetric[];
  groups: SpecGroup[];
  inBox: InBoxItem[];
  certifications: string[];
  pdfButton?: React.ReactNode;
}

/**
 * Polished technical specifications panel for REM7 Hydra products.
 * Three-part composition:
 *   1. Dimension overview — large hero image + key measurement chips
 *   2. Grouped spec table — sectioned by system (size, hydro, control, build, logistics)
 *   3. What's in the box — included plumbing & accessories grid
 */
export const Rem7HydraSpecsPanel = ({
  shortName,
  productName,
  sku,
  heroImage,
  dimensions,
  groups,
  inBox,
  certifications,
  pdfButton,
}: Rem7HydraSpecsPanelProps) => {
  return (
    <section
      id="specifications"
      className="border-b border-neutral-200 bg-white py-20"
    >
      <div className="container mx-auto px-6 lg:px-12">
        {/* HEADER */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">
            Technical Specifications
          </p>
          <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
            {shortName} — Engineered to the Millimetre
          </h2>
          <div className="mx-auto mt-3 h-px w-16 bg-amber-500" />
          <p className="mt-5 text-neutral-600 leading-relaxed">
            Full technical breakdown of the {productName}. Specify with
            confidence — every measurement, pump rating, certification and
            included component is listed below for your design and procurement
            teams.
          </p>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
            Reference SKU · {sku}
          </p>
        </div>

        {/* PART 1 — DIMENSION OVERVIEW */}
        <div className="mx-auto mt-14 grid max-w-6xl gap-8 overflow-hidden rounded-2xl border border-neutral-200 bg-gradient-to-br from-neutral-50 to-white shadow-sm lg:grid-cols-5">
          <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100 lg:col-span-3 lg:aspect-auto">
            <OptimizedImage
              src={heroImage.src}
              alt={heroImage.alt}
              width={1200}
              height={900}
              className="h-full w-full"
            />
            <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full border border-amber-500/40 bg-neutral-900/85 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-amber-300 backdrop-blur">
              <Ruler className="h-3.5 w-3.5" />
              Dimensional Drawing Reference
            </div>
          </div>

          <div className="flex flex-col justify-center gap-5 p-8 lg:col-span-2 lg:p-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-600">
              Key Measurements
            </p>
            <h3 className="font-serif text-xl font-bold text-neutral-900">
              Compact rough-in, full spa footprint
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {dimensions.map((d) => (
                <div
                  key={d.label}
                  className="rounded-lg border border-neutral-200 bg-white p-4"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    {d.label}
                  </p>
                  <p className="mt-1 font-serif text-2xl font-bold text-neutral-900">
                    {d.value}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-amber-600">
                    {d.unit}
                  </p>
                </div>
              ))}
            </div>
            {pdfButton && (
              <div className="mt-2">{pdfButton}</div>
            )}
          </div>
        </div>

        {/* PART 2 — GROUPED SPEC TABLE */}
        <div className="mx-auto mt-14 max-w-6xl">
          <div className="mb-6 flex items-center justify-between gap-4 border-b border-neutral-200 pb-4">
            <div className="flex items-center gap-3">
              <Settings2 className="h-5 w-5 text-amber-600" />
              <h3 className="font-serif text-xl font-bold text-neutral-900">
                Full Technical Breakdown
              </h3>
            </div>
            <div className="hidden items-center gap-2 sm:flex">
              {certifications.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-amber-500/40 bg-amber-50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-700"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {groups.map((g) => (
              <div
                key={g.id}
                className="rounded-xl border border-neutral-200 bg-white shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex items-center gap-3 border-b border-neutral-200 bg-neutral-50/60 px-5 py-3">
                  <g.icon className="h-4 w-4 text-amber-600" />
                  <h4 className="font-serif text-base font-semibold text-neutral-900">
                    {g.title}
                  </h4>
                </div>
                <dl className="divide-y divide-neutral-100">
                  {g.rows.map((row) => (
                    <div
                      key={row.label}
                      className="flex flex-col gap-1 px-5 py-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6"
                    >
                      <dt className="text-xs font-medium uppercase tracking-[0.1em] text-neutral-500">
                        {row.label}
                      </dt>
                      <dd className="text-right text-sm text-neutral-900 sm:max-w-[60%]">
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>

          {/* Mobile certification chips */}
          <div className="mt-6 flex flex-wrap gap-2 sm:hidden">
            {certifications.map((c) => (
              <span
                key={c}
                className="rounded-full border border-amber-500/40 bg-amber-50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-700"
              >
                {c}
              </span>
            ))}
          </div>
        </div>

        {/* PART 3 — WHAT'S IN THE BOX */}
        <div className="mx-auto mt-14 max-w-6xl rounded-2xl border border-neutral-200 bg-neutral-900 p-8 text-white shadow-lg lg:p-12">
          <div className="flex flex-col gap-2 border-b border-white/10 pb-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300">
                What's in the Box
              </p>
              <h3 className="mt-2 font-serif text-2xl font-bold">
                Included with every {shortName}
              </h3>
            </div>
            <p className="text-xs text-neutral-400 sm:max-w-xs sm:text-right">
              Standard EU plumbing pack ships with each unit. No exotic parts —
              install with conventional rough-in.
            </p>
          </div>

          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {inBox.map((item) => (
              <li
                key={item.label}
                className="flex items-start gap-4 rounded-lg border border-white/10 bg-white/[0.03] p-4 transition-colors hover:border-amber-500/40 hover:bg-white/[0.06]"
              >
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border border-amber-500/30 bg-amber-500/10">
                  <item.icon className="h-5 w-5 text-amber-300" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-sm font-semibold text-white">
                    {item.label}
                  </p>
                  {item.qty && (
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300">
                      {item.qty}
                    </p>
                  )}
                </div>
                <CheckCircle2 className="mt-1 h-4 w-4 flex-shrink-0 text-amber-300" />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export const HydraSpecIcons = {
  Ruler,
  Zap,
  Gauge,
  Package,
  ShieldCheck,
  Settings2,
  Droplets,
  Sparkles,
  Radio,
  Wrench,
};
